import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sun, Moon } from "lucide-react";
import { NAV } from "@/lib/content";
import { cn } from "@/lib/utils";

function ThemeToggle() {
  const [theme, setTheme] = useState(() => localStorage.getItem("fe-theme") || "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("fe-theme", theme);
  }, [theme]);
  return (
    <button
      data-testid="theme-toggle"
      aria-label="Toggle theme"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="grid place-items-center h-10 w-10 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled ? "py-3" : "py-5"
      )}
    >
      <div
        className={cn(
          "mx-auto max-w-7xl px-4 md:px-6 transition-all duration-300",
          scrolled ? "" : ""
        )}
      >
        <div
          className={cn(
            "flex items-center justify-between rounded-full px-3 md:px-5 py-2.5 transition-all duration-300",
            scrolled ? "glass-strong border border-white/[0.08]" : "border border-transparent"
          )}
        >
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 pl-2">
            <div className="h-8 w-8 rounded-full overflow-hidden border border-white/20 bg-white flex items-center justify-center shrink-0">
              <img src="/company.jpg" alt="Fullstack Endeavours" className="w-full h-full object-contain" />
            </div>
            <span className="font-display text-lg tracking-tight">Fullstack Endeavours</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.href}
                to={n.href}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "relative px-4 py-2 text-sm font-medium rounded-full transition",
                    isActive
                      ? "text-foreground bg-white/[0.06]"
                      : "text-zinc-400 hover:text-foreground hover:bg-white/[0.04]"
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link
              to="/contact"
              data-testid="nav-cta-start-project"
              className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-4 py-2 text-sm hover:brightness-110 transition"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((s) => !s)}
              className="md:hidden grid place-items-center h-10 w-10 rounded-full border border-white/10 bg-white/[0.03]"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="md:hidden mx-4 mt-2 rounded-2xl glass-strong border border-white/[0.08] overflow-hidden"
          >
            <div className="p-2">
              {NAV.map((n) => (
                <NavLink
                  key={n.href}
                  to={n.href}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    cn(
                      "block px-4 py-3 rounded-xl text-sm",
                      isActive ? "bg-white/[0.06] text-foreground" : "text-zinc-400"
                    )
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-start"
                className="mt-1 mx-1 inline-flex w-[calc(100%-8px)] items-center justify-center gap-1.5 rounded-xl bg-nexus-cyan text-nexus-ink font-semibold px-4 py-3 text-sm"
              >
                Start a project <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
